import { useTrans } from "src/utils/useTrans";
import UList from "../components/UList";
import Link from "next/link";
import { Button } from "@/v2-components/ui/button";
import ExternalLinkIcon from "@/v2-components/core/icon/external-link-icon";
import React, { ReactNode } from "react";
import { Links } from "@/constants";
import DownloadIcon from "@/v2-components/core/icon/download-icon";
import Image from "next/image";
import { usePCPLatestRelease } from "@/queries/pcp-release-query";

const useTranslateSupportPlayerSection = () => {
  const { t } = useTrans(["support"]);
  const { win, mac } = usePCPLatestRelease();

  const Script: {
    imgLink: string;
    title: string;
    for: string;
    list: string[];
    contents: ReactNode;
  }[] = [
    /**
     * ★**Standalone VR** — Meta Quest 한 기종 카드였던 것을 세 헤드셋 스토어로 넓혔다
     * (2026-09-29 작성자 지시 · 리뉴얼된 `/games/vr/software` 의 「For Standalone VR」과 같은
     * 묶음). 스토어 링크는 **그 페이지와 같은 상수**(`sw-config.ts` 의 `stores`)다 — 새로
     * 적지 않는다. 「from App Lab」은 App Lab 이 Meta Store 로 통합돼 걷었다.
     * 이미지는 기존 Meta Quest 컷을 그대로 쓴다(세 기종 컷은 없다).
     */
    {
      imgLink: "/images/support/downloads/download-player-meta.jpg",
      title: "Standalone VR",
      for: "Meta Quest, PICO, HTC VIVE",
      list: [
        t("Check device connection status"),
        t("Change device configuration"),
        t("Try haptic demo"),
        t("Test haptic feedback"),
      ],
      // 스토어 이름은 고유명사라 번역하지 않는다 — `/games/vr/software` 도 같다.
      contents: (
        <div className="flex w-full flex-wrap gap-2">
          <DownloadPlayerLinkButton
            text="Meta Store"
            link={Links.BhapticsMetaQuest}
          />
          <DownloadPlayerLinkButton
            text="PICO Store"
            link={Links.VRPPicoStore}
          />
          <DownloadPlayerLinkButton text="VIVEPORT" link={Links.VRPVivePort} />
        </div>
      ),
    },
    {
      imgLink: "/images/support/downloads/download-player-window.jpg",
      title: "PC",
      /**
       * 신 PC Player(3.x · Tauri/WebView2) 의 **실제** 최소 OS (#5769 · 2026-10-08 `bhaptics-dev/pc-player`
       * develop `5476d4855` 기준). 설치 프로그램·앱 모두 OS 버전을 검사하지 않아, 미달 기기는 설치는 되고
       * 실행에서 조용히 실패한다 — 그래서 이 문구가 유일한 안내다.
       *   · Windows 1709+ : WebView2 지원 범위 「Windows 10 SAC 1709 and later」·Windows 11(Microsoft Learn ·
       *     webview2 「Supported Windows versions」). Rust 1.78+ 의 Windows 10 최소보다 이쪽이 엄격하다.
       *     종전 값은 「1703」이었다(출처 미확인 — 구 PCP Inno 설정 `MinVersion=10.0.10240` 과도 다르다).
       *   · macOS 13+ : 빌드가 `MACOSX_DEPLOYMENT_TARGET: "13.0"`(pc-player `.github/workflows/_build-tauri.yml`)
       *     — 종전 「12 (Monterey)」는 그 아래다. QA 템플릿의 테스트 OS 도 macOS 13·14·15.
       * 확인: pc-player 에서 `git grep -n MACOSX_DEPLOYMENT_TARGET origin/develop -- .github/workflows`
       */
      for: "Windows 10 (ver 1709 and later), Windows 11\nmacOS 13 (Ventura) and later",
      list: [
        t("Connect and manage bHaptics devices via Bluetooth"),
        t("Install game mods for haptic feedback in supported games"),
        t("Explore the haptic experiences library"),
        t("Use Audio-to-Haptic to feel music and sound effects"),
        t("Adjust haptic intensity and device settings"),
        t("Quick Control Panel for fast access to key features"),
        t("Manage your account and troubleshoot issues"),
      ],
      contents: (
        <div className="flex w-full flex-wrap gap-2">
          <DownloadPlayerDownloadButton text={t("Windows")} link={win} />
          <DownloadPlayerDownloadButton text={t("macOS")} link={mac} />
        </div>
      ),
    },
    {
      imgLink: "/images/support/downloads/download-player-mobile.jpg",
      title: "Mobile",
      for: "iOS, Androids",
      list: [
        t("Pair bHaptics device to mobile"),
        t("Test and experience haptic patterns"),
        t("Connected to the same network as PSVR2"),
        t("Visualize haptic feedback"),
      ],
      contents: (
        <div className="flex w-full gap-2">
          <DownloadPlayerLinkButton text={t("iOS")} link={Links.BhapticsIOS} />
          <DownloadPlayerLinkButton
            text={t("Android")}
            link={Links.BhapticsAndroid}
          />
        </div>
      ),
    },
  ];

  return Script;
};

export default function BhapticsPlayerSection() {
  const DATA = useTranslateSupportPlayerSection();

  return (
    <>
      <div className="flex flex-col gap-y-[50px] xsm:gap-y-[80px]">
        {DATA.map((item, index) => {
          return (
            <div key={index} className="flex flex-col gap-5">
              <div className="flex flex-col flex-wrap gap-y-1 xsm:justify-between xsm:gap-y-0.5 lg:flex-row lg:items-center">
                <h3 className="whitespace-nowrap text-lg font-500 sm:text-2xl sm:font-400">
                  {item.title}
                </h3>

                <div className="flex xsm:justify-end">
                  <p className="whitespace-pre text-sm font-400 xsm:whitespace-normal">
                    {item.for}
                  </p>
                </div>
              </div>

              <div className="flex flex-col smd:flex-row">
                {/*<figure>*/}
                <figure className="relative flex min-h-[328px] w-full items-center justify-center smd:min-h-[280px] smd:min-w-0 smd:flex-1">
                  <Image
                    alt={""}
                    src={item.imgLink}
                    sizes="50vw"
                    className={
                      "w-full rounded-t-2xl object-cover smd:rounded-l-2xl smd:rounded-r-none"
                    }
                    fill
                  />
                </figure>

                {/*contents*/}
                <div className="min-h-fit w-full overflow-x-auto rounded-b-2xl bg-coolgray-10 px-4 py-5 sm:px-6 sm:py-6 smd:min-w-0 smd:flex-1 smd:rounded-l-none smd:rounded-r-2xl">
                  <div className="flex h-full flex-col justify-between gap-y-6 smd:gap-y-2.5">
                    <div className="flex flex-col gap-y-0.5">
                      <UList list={item.list} />
                    </div>
                    <div className="flex flex-nowrap gap-x-1">
                      {item.contents}
                    </div>
                  </div>
                </div>
              </div>

              {/*</div>*/}
            </div>
          );
        })}
      </div>
    </>
  );
}

const DownloadPlayerLinkButton = ({
  text,
  link,
}: {
  text: string;
  link: string;
}) => {
  return (
    <Link href={link} target="_blank">
      <Button
        variant="darkgray-fill"
        size="sm"
        shape="pill"
        className="h-[42px] w-[128px] text-sm"
      >
        {text}
        <ExternalLinkIcon className="y-5 w-5" />
      </Button>
    </Link>
  );
};

const DownloadPlayerDownloadButton = ({
  text,
  link,
}: {
  text: string;
  link: string;
}) => {
  return (
    <Link href={link} target="_self">
      <Button
        variant="darkgray-text"
        size="sm"
        className="h-[42px] min-w-[128px] border border-coolgray-90 transition-all hover:bg-coolgray-90 hover:text-white"
      >
        {text}
        <DownloadIcon className="y-5 mb-1 w-5 min-w-5" />
      </Button>
    </Link>
  );
};
